import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Bell,
  CheckCircle,
  GraduationCap,
  Mail,
  Share2,
  Ticket,
  User,
} from "lucide-react";
import {
  useNotificationsContext,
  type Notification,
} from "../../contexts/NotificationsContext";
import { isSafeInternalPath } from "../../lib/postLoginRedirect";

// Presentación y apertura de una notificación, compartidas por la página de
// Notificaciones y la campanita de la barra.

export const NotificationIcon: React.FC<{ type: Notification["type"] }> = ({
  type,
}) => {
  switch (type) {
    case "ticket_created":
      return <Ticket className="h-4 w-4 shrink-0 text-sky-500" />;
    case "ticket_assigned":
      return <User className="h-4 w-4 shrink-0 text-blue-500" />;
    case "ticket_shared":
      return <Share2 className="h-4 w-4 shrink-0 text-indigo-500" />;
    case "status_changed":
      return <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />;
    case "comment_added":
      return <Mail className="h-4 w-4 shrink-0 text-purple-500" />;
    case "priority_changed":
      return <AlertCircle className="h-4 w-4 shrink-0 text-orange-500" />;
    case "workshop_available":
      return <GraduationCap className="h-4 w-4 shrink-0 text-amber-500" />;
    default:
      return <Bell className="h-4 w-4 shrink-0 text-gray-500" />;
  }
};

/** "Ticket #00123" con el número real; null si no hay ticket o no se sabe. */
export const notificationTicketLabel = (n: Notification): string | null => {
  const num = n.ticketNumber ?? n.metadata?.ticketNumber;
  return typeof num === "number"
    ? `Ticket #${num.toString().padStart(5, "0")}`
    : null;
};

export const formatNotificationDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60),
  );

  if (diffInMinutes < 1) return "Ahora mismo";
  if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
  if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)}h`;
  return date.toLocaleDateString("es-ES");
};

/** Adónde lleva, o null si no lleva a ningún lado (o el ticket ya no existe). */
export const notificationDestination = (n: Notification): string | null =>
  !n.targetMissing && isSafeInternalPath(n.link) ? n.link : null;

/** Tocar una notificación: se marca como leída (optimista) y se navega. */
export const useOpenNotification = () => {
  const navigate = useNavigate();
  const { markAsRead } = useNotificationsContext();
  return React.useCallback(
    (n: Notification) => {
      if (!n.read) void markAsRead(n.id);
      const to = notificationDestination(n);
      if (to) navigate(to);
    },
    [markAsRead, navigate],
  );
};

/** Props para que un bloque entero funcione como link accesible (Enter/Espacio). */
export const linkLikeProps = (onOpen: () => void) => ({
  role: "link" as const,
  tabIndex: 0,
  onClick: onOpen,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  },
});
